import { motion } from 'framer-motion';
import { PresentationSlide, ThemePreset, TypographySettings, AnimationSettings } from '../../types';

interface SlideLayoutRendererProps {
  slide: PresentationSlide;
  theme: ThemePreset;
  typography: TypographySettings;
  animations: AnimationSettings;
  isFullscreen?: boolean;
}

// Animation helper functions
const getTextAnimationInitial = (animation: string) => {
  switch (animation) {
    case 'none': return { opacity: 1, y: 0, scale: 1 };
    case 'typewriter': return { opacity: 1, width: '0%' };
    case 'fade-in-up': return { opacity: 0, y: 30 };
    case 'fade-in-down': return { opacity: 0, y: -30 };
    case 'bounce-in': return { opacity: 0, scale: 0.3 };
    case 'zoom-in': return { opacity: 0, scale: 0.5 };
    default: return { opacity: 1 };
  }
};

const getTextAnimationAnimate = (animation: string) => {
  switch (animation) {
    case 'none': return { opacity: 1, y: 0, scale: 1 };
    case 'typewriter': return { opacity: 1, width: '100%' };
    case 'fade-in-up': return { opacity: 1, y: 0 };
    case 'fade-in-down': return { opacity: 1, y: 0 };
    case 'bounce-in': return { opacity: 1, scale: 1 };
    case 'zoom-in': return { opacity: 1, scale: 1 };
    default: return { opacity: 1 };
  }
};

const getElementAnimationInitial = (animation: string) => {
  switch (animation) {
    case 'none': return { opacity: 1, y: 0, x: 0, scale: 1, rotateX: 0 };
    case 'fade-in': return { opacity: 0 };
    case 'slide-up': return { opacity: 0, y: 30 };
    case 'slide-left': return { opacity: 0, x: 50 };
    case 'slide-right': return { opacity: 0, x: -50 };
    case 'zoom-in': return { opacity: 0, scale: 0.5 };
    case 'bounce': return { opacity: 0, y: 30 };
    case 'flip': return { opacity: 0, rotateX: -90 };
    default: return { opacity: 1 };
  }
};

const getElementAnimationAnimate = (animation: string) => {
  switch (animation) {
    case 'none': return { opacity: 1, y: 0, x: 0, scale: 1, rotateX: 0 };
    case 'fade-in': return { opacity: 1 };
    case 'slide-up': return { opacity: 1, y: 0 };
    case 'slide-left': return { opacity: 1, x: 0 };
    case 'slide-right': return { opacity: 1, x: 0 };
    case 'zoom-in': return { opacity: 1, scale: 1 };
    case 'bounce': return { opacity: 1, y: 0 };
    case 'flip': return { opacity: 1, rotateX: 0 };
    default: return { opacity: 1 };
  }
};

export default function SlideLayoutRenderer({
  slide,
  theme,
  typography,
  animations,
  isFullscreen = false,
}: SlideLayoutRendererProps) {
  const layout = slide.layout || 'default';
  const transitionDuration = animations.slideTransitionDuration / 1000;

  const titleStyle = {
    color: theme.colors.primary,
    fontFamily: typography.headingFont,
    fontWeight: typography.headingWeight === 'extrabold' ? 800 : typography.headingWeight === 'bold' ? 700 : typography.headingWeight === 'medium' ? 500 : 400,
    fontSize: isFullscreen
      ? typography.headingSize === 'xlarge' ? '4rem' : typography.headingSize === 'large' ? '3.5rem' : typography.headingSize === 'medium' ? '3rem' : '2.5rem'
      : typography.headingSize === 'xlarge' ? '3rem' : typography.headingSize === 'large' ? '2.5rem' : typography.headingSize === 'medium' ? '2rem' : '1.5rem',
  };

  const subtitleStyle = {
    color: theme.colors.accent,
    fontFamily: typography.bodyFont,
    fontSize: isFullscreen ? '1.5rem' : '1.25rem',
  };

  const textStyle = {
    color: theme.colors.text,
    fontFamily: typography.bodyFont,
    fontSize: isFullscreen ? '1.25rem' : '1rem',
  };

  const bulletColor = theme.colors.accent;
  const elementAnimation = slide.animation || animations.elementAnimation;

  // Title Only Layout (with optional background image)
  if (layout === 'title' || slide.type === 'title') {
    return (
      <div
        className="h-full flex flex-col items-center justify-center text-center px-8 relative"
        style={slide.image ? {
          backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.7)), url(${slide.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        } : {}}
      >
        <motion.h1
          initial={getTextAnimationInitial(animations.textAnimation)}
          animate={getTextAnimationAnimate(animations.textAnimation)}
          transition={{ duration: transitionDuration, ease: 'easeOut' }}
          style={{
            ...titleStyle,
            fontSize: isFullscreen ? '5rem' : '3.5rem',
            color: slide.image ? '#ffffff' : titleStyle.color,
            textShadow: slide.image ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none',
          }}
          className="mb-6"
        >
          {slide.title}
        </motion.h1>
        {slide.subtitle && (
          <motion.p
            initial={getTextAnimationInitial(animations.textAnimation)}
            animate={getTextAnimationAnimate(animations.textAnimation)}
            transition={{ duration: transitionDuration, delay: 0.1, ease: 'easeOut' }}
            style={{
              ...subtitleStyle,
              fontSize: isFullscreen ? '2rem' : '1.5rem',
              color: slide.image ? 'rgba(255,255,255,0.9)' : subtitleStyle.color,
            }}
          >
            {slide.subtitle}
          </motion.p>
        )}
      </div>
    );
  }

  // Full Image Layout - Image as background with text overlay
  if (layout === 'full-image') {
    return (
      <div
        className="h-full relative flex flex-col justify-end"
        style={{
          backgroundImage: slide.image
            ? `linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.1) 100%), url(${slide.image})`
            : `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.background} 100%)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="p-8 md:p-12">
          <motion.h2
            initial={getTextAnimationInitial(animations.textAnimation)}
            animate={getTextAnimationAnimate(animations.textAnimation)}
            transition={{ duration: transitionDuration, ease: 'easeOut' }}
            style={{
              ...titleStyle,
              color: '#ffffff',
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
            }}
            className="mb-4"
          >
            {slide.title}
          </motion.h2>
          {slide.subtitle && (
            <motion.p
              initial={getTextAnimationInitial(animations.textAnimation)}
              animate={getTextAnimationAnimate(animations.textAnimation)}
              transition={{ duration: transitionDuration, delay: 0.1, ease: 'easeOut' }}
              style={{
                ...subtitleStyle,
                color: 'rgba(255,255,255,0.9)',
              }}
              className="mb-4"
            >
              {slide.subtitle}
            </motion.p>
          )}
          {slide.content && slide.content.length > 0 && (
            <ul className="space-y-2">
              {slide.content.slice(0, 3).map((item, i) => (
                <motion.li
                  key={i}
                  initial={getElementAnimationInitial(elementAnimation)}
                  animate={getElementAnimationAnimate(elementAnimation)}
                  transition={{ duration: transitionDuration, delay: 0.2 + (i * 0.1), ease: 'easeOut' }}
                  className="flex items-start gap-3"
                  style={{
                    ...textStyle,
                    color: 'rgba(255,255,255,0.9)',
                  }}
                >
                  <span className="w-2 h-2 rounded-full mt-2 flex-shrink-0 bg-white/70" />
                  <span>{item}</span>
                </motion.li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  // Image Left Layout - Image on left, content on right
  if (layout === 'image-left') {
    return (
      <div className="h-full grid grid-cols-2 overflow-hidden">
        {/* Image Side */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: transitionDuration, ease: 'easeOut' }}
          className="relative overflow-hidden flex items-center justify-center p-4"
          style={{ background: theme.colors.background }}
        >
          {slide.image ? (
            <img
              src={slide.image}
              alt={slide.imageAlt || slide.title}
              className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
            />
          ) : (
            <div
              className="w-full h-full rounded-lg"
              style={{
                background: `linear-gradient(135deg, ${theme.colors.primary}40 0%, ${theme.colors.secondary}40 100%)`
              }}
            />
          )}
        </motion.div>

        {/* Content Side */}
        <div className="p-4 md:p-8 flex flex-col overflow-hidden">
          <motion.h2
            initial={getTextAnimationInitial(animations.textAnimation)}
            animate={getTextAnimationAnimate(animations.textAnimation)}
            transition={{ duration: transitionDuration, ease: 'easeOut' }}
            style={titleStyle}
            className="mb-2 flex-shrink-0"
          >
            {slide.title}
          </motion.h2>
          {slide.subtitle && (
            <motion.p
              initial={getTextAnimationInitial(animations.textAnimation)}
              animate={getTextAnimationAnimate(animations.textAnimation)}
              transition={{ duration: transitionDuration, delay: 0.1, ease: 'easeOut' }}
              style={subtitleStyle}
              className="mb-3 flex-shrink-0"
            >
              {slide.subtitle}
            </motion.p>
          )}
          <ul className="space-y-2 overflow-y-auto flex-1 pr-2">
            {slide.content.map((item, i) => (
              <motion.li
                key={i}
                initial={getElementAnimationInitial(elementAnimation)}
                animate={getElementAnimationAnimate(elementAnimation)}
                transition={{ duration: transitionDuration, delay: 0.2 + (i * 0.1), ease: 'easeOut' }}
                className="flex items-start gap-3"
                style={textStyle}
              >
                <span className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: bulletColor }} />
                <span>{item}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  // Image Right Layout - Content on left, image on right
  if (layout === 'image-right') {
    return (
      <div className="h-full grid grid-cols-2 overflow-hidden">
        {/* Content Side */}
        <div className="p-4 md:p-8 flex flex-col overflow-hidden">
          <motion.h2
            initial={getTextAnimationInitial(animations.textAnimation)}
            animate={getTextAnimationAnimate(animations.textAnimation)}
            transition={{ duration: transitionDuration, ease: 'easeOut' }}
            style={titleStyle}
            className="mb-2 flex-shrink-0"
          >
            {slide.title}
          </motion.h2>
          {slide.subtitle && (
            <motion.p
              initial={getTextAnimationInitial(animations.textAnimation)}
              animate={getTextAnimationAnimate(animations.textAnimation)}
              transition={{ duration: transitionDuration, delay: 0.1, ease: 'easeOut' }}
              style={subtitleStyle}
              className="mb-3 flex-shrink-0"
            >
              {slide.subtitle}
            </motion.p>
          )}
          <ul className="space-y-2 overflow-y-auto flex-1 pr-2">
            {slide.content.map((item, i) => (
              <motion.li
                key={i}
                initial={getElementAnimationInitial(elementAnimation)}
                animate={getElementAnimationAnimate(elementAnimation)}
                transition={{ duration: transitionDuration, delay: 0.2 + (i * 0.1), ease: 'easeOut' }}
                className="flex items-start gap-3"
                style={textStyle}
              >
                <span className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: bulletColor }} />
                <span>{item}</span>
              </motion.li>
            ))}
          </ul>
        </div>

        {/* Image Side */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: transitionDuration, ease: 'easeOut' }}
          className="relative overflow-hidden flex items-center justify-center p-4"
          style={{ background: theme.colors.background }}
        >
          {slide.image ? (
            <img
              src={slide.image}
              alt={slide.imageAlt || slide.title}
              className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
            />
          ) : (
            <div
              className="w-full h-full rounded-lg"
              style={{
                background: `linear-gradient(135deg, ${theme.colors.primary}40 0%, ${theme.colors.secondary}40 100%)`
              }}
            />
          )}
        </motion.div>
      </div>
    );
  }

  // Two Column Layout
  if (layout === 'two-column') {
    const midpoint = Math.ceil(slide.content.length / 2);
    const leftColumn = slide.content.slice(0, midpoint);
    const rightColumn = slide.content.slice(midpoint);

    return (
      <div className="h-full p-6 md:p-10 flex flex-col overflow-hidden">
        <motion.h2
          initial={getTextAnimationInitial(animations.textAnimation)}
          animate={getTextAnimationAnimate(animations.textAnimation)}
          transition={{ duration: transitionDuration, ease: 'easeOut' }}
          style={titleStyle}
          className="mb-4 flex-shrink-0"
        >
          {slide.title}
        </motion.h2>
        <div className="flex-1 grid grid-cols-2 gap-6 overflow-y-auto">
          <div className="space-y-2">
            {leftColumn.map((item, i) => (
              <motion.div
                key={i}
                initial={getElementAnimationInitial(elementAnimation)}
                animate={getElementAnimationAnimate(elementAnimation)}
                transition={{ duration: transitionDuration, delay: 0.2 + (i * 0.1), ease: 'easeOut' }}
                className="flex items-start gap-3"
                style={textStyle}
              >
                <span className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: bulletColor }} />
                <span>{item}</span>
              </motion.div>
            ))}
          </div>
          <div className="space-y-2">
            {rightColumn.map((item, i) => (
              <motion.div
                key={i}
                initial={getElementAnimationInitial(elementAnimation)}
                animate={getElementAnimationAnimate(elementAnimation)}
                transition={{ duration: transitionDuration, delay: 0.2 + ((i + midpoint) * 0.1), ease: 'easeOut' }}
                className="flex items-start gap-3"
                style={textStyle}
              >
                <span className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: bulletColor }} />
                <span>{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Quote Layout
  if (layout === 'quote' || slide.type === 'quote') {
    return (
      <div className="h-full flex flex-col items-center justify-center px-12 md:px-24 text-center">
        <motion.div
          initial={getTextAnimationInitial(animations.textAnimation)}
          animate={getTextAnimationAnimate(animations.textAnimation)}
          transition={{ duration: transitionDuration, ease: 'easeOut' }}
          className="relative"
        >
          <span
            className="absolute -top-8 -left-8 text-6xl opacity-30"
            style={{ color: theme.colors.primary, fontFamily: 'serif' }}
          >
            "
          </span>
          <p
            style={{
              ...textStyle,
              fontSize: isFullscreen ? '2rem' : '1.5rem',
              fontStyle: 'italic',
              lineHeight: 1.6,
            }}
          >
            {slide.content[0]}
          </p>
          <span
            className="absolute -bottom-8 -right-8 text-6xl opacity-30"
            style={{ color: theme.colors.primary, fontFamily: 'serif' }}
          >
            "
          </span>
        </motion.div>
        {slide.subtitle && (
          <motion.p
            initial={getElementAnimationInitial(elementAnimation)}
            animate={getElementAnimationAnimate(elementAnimation)}
            transition={{ duration: transitionDuration, delay: 0.3, ease: 'easeOut' }}
            className="mt-8"
            style={{ color: theme.colors.accent, fontFamily: typography.headingFont }}
          >
            — {slide.subtitle}
          </motion.p>
        )}
      </div>
    );
  }

  // Section Divider Layout
  if (layout === 'section-divider' || slide.type === 'section') {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center">
        <motion.div
          initial={getElementAnimationInitial(elementAnimation)}
          animate={getElementAnimationAnimate(elementAnimation)}
          transition={{ duration: transitionDuration, ease: 'easeOut' }}
          className="w-16 h-1 mb-8 rounded-full"
          style={{ background: theme.colors.primary }}
        />
        <motion.h2
          initial={getTextAnimationInitial(animations.textAnimation)}
          animate={getTextAnimationAnimate(animations.textAnimation)}
          transition={{ duration: transitionDuration, delay: 0.1, ease: 'easeOut' }}
          style={{ ...titleStyle, fontSize: isFullscreen ? '4rem' : '3rem' }}
        >
          {slide.title}
        </motion.h2>
        {slide.subtitle && (
          <motion.p
            initial={getTextAnimationInitial(animations.textAnimation)}
            animate={getTextAnimationAnimate(animations.textAnimation)}
            transition={{ duration: transitionDuration, delay: 0.2, ease: 'easeOut' }}
            className="mt-4"
            style={subtitleStyle}
          >
            {slide.subtitle}
          </motion.p>
        )}
      </div>
    );
  }

  // Stats/Numbers Layout
  if (layout === 'stats') {
    return (
      <div className="h-full p-8 md:p-12 flex flex-col">
        <motion.h2
          initial={getTextAnimationInitial(animations.textAnimation)}
          animate={getTextAnimationAnimate(animations.textAnimation)}
          transition={{ duration: transitionDuration, ease: 'easeOut' }}
          style={titleStyle}
          className="mb-8 text-center"
        >
          {slide.title}
        </motion.h2>
        <div className="flex-1 grid grid-cols-3 gap-6 items-center">
          {slide.content.slice(0, 3).map((item, i) => {
            const [value, label] = item.includes(':') ? item.split(':') : [item, ''];
            return (
              <motion.div
                key={i}
                initial={getElementAnimationInitial(elementAnimation)}
                animate={getElementAnimationAnimate(elementAnimation)}
                transition={{ duration: transitionDuration, delay: 0.2 + (i * 0.15), ease: 'easeOut' }}
                className="text-center"
              >
                <div style={{ ...titleStyle, fontSize: isFullscreen ? '4rem' : '3rem', color: theme.colors.primary }}>
                  {value.trim()}
                </div>
                {label && (
                  <div style={{ ...textStyle, marginTop: '0.5rem' }}>
                    {label.trim()}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    );
  }

  // Timeline Layout
  if (layout === 'timeline' || slide.type === 'timeline') {
    return (
      <div className="h-full p-6 md:p-10 flex flex-col overflow-hidden">
        <motion.h2
          initial={getTextAnimationInitial(animations.textAnimation)}
          animate={getTextAnimationAnimate(animations.textAnimation)}
          transition={{ duration: transitionDuration, ease: 'easeOut' }}
          style={titleStyle}
          className="mb-6 flex-shrink-0"
        >
          {slide.title}
        </motion.h2>
        <div className="flex-1 relative overflow-y-auto">
          {/* Timeline line */}
          <div
            className="absolute left-4 top-0 bottom-0 w-0.5"
            style={{ background: theme.colors.secondary }}
          />
          <div className="space-y-4 pl-12 pr-2">
            {slide.content.map((item, i) => (
              <motion.div
                key={i}
                initial={getElementAnimationInitial(elementAnimation)}
                animate={getElementAnimationAnimate(elementAnimation)}
                transition={{ duration: transitionDuration, delay: 0.2 + (i * 0.15), ease: 'easeOut' }}
                className="relative"
              >
                {/* Timeline dot */}
                <div
                  className="absolute -left-12 top-2 w-4 h-4 rounded-full border-2"
                  style={{ background: theme.colors.background, borderColor: theme.colors.primary }}
                />
                <p style={textStyle}>{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Feature Grid 2x2
  if (layout === 'feature-grid-2x2') {
    return (
      <div className="h-full p-8 md:p-12 flex flex-col">
        <motion.h2
          initial={getTextAnimationInitial(animations.textAnimation)}
          animate={getTextAnimationAnimate(animations.textAnimation)}
          transition={{ duration: transitionDuration, ease: 'easeOut' }}
          style={titleStyle}
          className="mb-6 text-center"
        >
          {slide.title}
        </motion.h2>
        <div className="flex-1 grid grid-cols-2 gap-4">
          {slide.content.slice(0, 4).map((item, i) => (
            <motion.div
              key={i}
              initial={getElementAnimationInitial(elementAnimation)}
              animate={getElementAnimationAnimate(elementAnimation)}
              transition={{ duration: transitionDuration, delay: 0.2 + (i * 0.1), ease: 'easeOut' }}
              className="p-4 rounded-xl"
              style={{ background: theme.colors.secondary }}
            >
              <div className="w-8 h-8 rounded-lg mb-3" style={{ background: theme.colors.primary }} />
              <p style={textStyle}>{item}</p>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  // Thank You Layout
  if (layout === 'thank-you') {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center">
        <motion.h1
          initial={getTextAnimationInitial(animations.textAnimation)}
          animate={getTextAnimationAnimate(animations.textAnimation)}
          transition={{ duration: transitionDuration, ease: 'easeOut' }}
          style={{ ...titleStyle, fontSize: isFullscreen ? '5rem' : '4rem' }}
          className="mb-4"
        >
          {slide.title || 'Thank You'}
        </motion.h1>
        {slide.subtitle && (
          <motion.p
            initial={getTextAnimationInitial(animations.textAnimation)}
            animate={getTextAnimationAnimate(animations.textAnimation)}
            transition={{ duration: transitionDuration, delay: 0.1, ease: 'easeOut' }}
            style={subtitleStyle}
          >
            {slide.subtitle}
          </motion.p>
        )}
        {slide.content.length > 0 && (
          <motion.div
            initial={getElementAnimationInitial(elementAnimation)}
            animate={getElementAnimationAnimate(elementAnimation)}
            transition={{ duration: transitionDuration, delay: 0.2, ease: 'easeOut' }}
            className="mt-8"
            style={textStyle}
          >
            {slide.content[0]}
          </motion.div>
        )}
      </div>
    );
  }

  // Default Layout (title-bullets)
  return (
    <div className="h-full p-6 md:p-10 flex flex-col overflow-hidden">
      <motion.h2
        initial={getTextAnimationInitial(animations.textAnimation)}
        animate={getTextAnimationAnimate(animations.textAnimation)}
        transition={{ duration: transitionDuration, ease: 'easeOut' }}
        style={titleStyle}
        className="mb-3 flex-shrink-0"
      >
        {slide.title}
      </motion.h2>
      {slide.subtitle && (
        <motion.p
          initial={getTextAnimationInitial(animations.textAnimation)}
          animate={getTextAnimationAnimate(animations.textAnimation)}
          transition={{ duration: transitionDuration, delay: 0.1, ease: 'easeOut' }}
          style={subtitleStyle}
          className="mb-4 flex-shrink-0"
        >
          {slide.subtitle}
        </motion.p>
      )}
      <ul className="space-y-2 overflow-y-auto flex-1 pr-2">
        {slide.content.map((item, i) => (
          <motion.li
            key={i}
            initial={getElementAnimationInitial(elementAnimation)}
            animate={getElementAnimationAnimate(elementAnimation)}
            transition={{
              duration: transitionDuration,
              delay: 0.2 + (i * 0.1),
              ease: elementAnimation === 'bounce' ? [0.68, -0.55, 0.265, 1.55] : 'easeOut'
            }}
            className="flex items-start gap-3"
            style={textStyle}
          >
            <span
              className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
              style={{ background: bulletColor }}
            />
            <span>{item}</span>
          </motion.li>
        ))}
      </ul>
    </div>
  );
}
